import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import { PRODUCT_VISUAL_FRESHNESS, type ProductVisualCell } from "@nova-ai/shared"
import { RefreshCw, RotateCcw } from "lucide-react"

import type { ProductVisualRenderStatus } from "../../hooks/use-product-visual-render"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime } from "../../lib/format-date"
import type { ProductVisualRenderedPreview } from "../../services/use-product-visual-preview"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import { productVisualGapMessage } from "./product-visual-gap-message"
import { productVisualKindLabel } from "./product-visual-kind-control"
import type { EnlargedProductVisual } from "./product-visual-lightbox"
import { ProductVisualTemplateFreshnessBadge } from "./product-visual-template-freshness-badge"

type ProductVisualCellCardProps = {
  simpleSku: string
  visualKind: ProductVisualKind
  cell: ProductVisualCell
  /** The session-ephemeral JPEG this cell's Render action last produced. */
  preview: ProductVisualRenderedPreview | undefined
  renderStatus: ProductVisualRenderStatus
  renderError: string | null
  onRender?: () => void
  regenerationStatus?: "idle" | "pending"
  regenerationError?: string | null
  onRegenerate?: () => void
  onEnlarge: (image: EnlargedProductVisual) => void
}

// One simple × one visual kind. Render is offered on every cell, not only the
// renderable ones: a published image answers "what would it look like now", and
// a gap re-resolves its sources fresh, so a stale gap can flip to an image.
export function ProductVisualCellCard({
  simpleSku,
  visualKind,
  cell,
  preview,
  renderStatus,
  renderError,
  onRender,
  regenerationStatus = "idle",
  regenerationError = null,
  onRegenerate,
  onEnlarge,
}: ProductVisualCellCardProps) {
  const kindLabel = productVisualKindLabel(visualKind)
  const artwork = resolveArtwork(cell, preview)

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5">
        <h4 className="text-[13px] font-semibold tracking-tight text-foreground">{kindLabel}</h4>

        <FreshnessSignals cell={cell} />
      </header>

      <CellArtwork
        url={artwork.url}
        alt={`${kindLabel} visual for ${simpleSku}`}
        caption={`${simpleSku} · ${kindLabel}${artwork.isLivePreview ? " · live preview" : ""}`}
        gapReason={artwork.gapReason}
        emptyLabel={
          cell.freshness === PRODUCT_VISUAL_FRESHNESS.new ? "New" : "No image published yet."
        }
        onEnlarge={onEnlarge}
      />

      <div className="flex flex-1 flex-col gap-2 px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {metaLine(cell, artwork.isLivePreview ? preview : undefined)}
        </p>

        {artwork.freshGapReason ? (
          <p className="text-xs text-warning">
            Fresh render: {productVisualGapMessage(artwork.freshGapReason)}
          </p>
        ) : null}

        {renderError ? <p className="text-xs text-destructive">{renderError}</p> : null}

        {regenerationError ? <p className="text-xs text-destructive">{regenerationError}</p> : null}

        <FreshnessDetails cell={cell} />

        <div className="mt-auto flex flex-wrap gap-2">
          {onRender ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={renderStatus !== "idle"}
              onClick={onRender}
            >
              <RefreshCw
                className={renderStatus === "rendering" ? "size-3.5 animate-spin" : "size-3.5"}
              />

              {renderLabel({ cell, renderStatus, hasLivePreview: artwork.isLivePreview })}
            </Button>
          ) : null}

          {shouldShowRegenerate(cell, onRegenerate) ? (
            <Button size="sm" disabled={regenerationStatus === "pending"} onClick={onRegenerate}>
              <RotateCcw
                className={regenerationStatus === "pending" ? "size-3.5 animate-spin" : "size-3.5"}
              />

              {regenerationStatus === "pending" ? "Queueing…" : "Regenerate now"}
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  )
}

/** What the square shows. A live preview supersedes the persisted image in
 *  place, and a fresh render's own gap reason supersedes the persisted one —
 *  Retry re-resolves sources, so its answer is the newer truth.
 *
 *  A fresh gap on a cell that still has a persisted image has nowhere to go in
 *  the square, and an `outdated` image is exactly the cell whose fresh render
 *  most often gaps — so it comes back as `freshGapReason`, which the card states
 *  next to the kept image. Without it the render would end in silence. */
function resolveArtwork(
  cell: ProductVisualCell,
  preview: ProductVisualRenderedPreview | undefined
): {
  url: string | null
  gapReason: string | null
  freshGapReason: string | null
  isLivePreview: boolean
} {
  const livePreviewUrl = preview?.imageDataUrl ?? null
  const publishedUrl = cell.artifact?.url ?? null
  const persistedGap = cell.freshness === PRODUCT_VISUAL_FRESHNESS.gap ? cell.reason : null
  const url = livePreviewUrl ?? publishedUrl
  const freshGap = preview?.gapReason ?? null
  return {
    url,
    gapReason: preview ? freshGap : persistedGap,
    freshGapReason: url === null ? null : freshGap,
    isLivePreview: livePreviewUrl !== null,
  }
}

function FreshnessSignals({ cell }: { cell: ProductVisualCell }) {
  const tone = freshnessTone(cell)
  return (
    <div
      role="group"
      aria-label="Product visual status"
      className="flex max-w-full flex-wrap justify-end gap-1.5"
    >
      <StatusBadge tone={tone}>
        {cell.freshness === PRODUCT_VISUAL_FRESHNESS.new
          ? "New"
          : humanizeEnumLabel(cell.freshness)}
      </StatusBadge>

      {cell.replacement ? (
        <StatusBadge tone={cell.replacement.status === "failed" ? "destructive" : "info"}>
          {humanizeEnumLabel(cell.replacement.status)}
        </StatusBadge>
      ) : null}

      {cell.artifact ? <ProductVisualTemplateFreshnessBadge image={cell.artifact} /> : null}
    </div>
  )
}

/** The square the operator actually judges the visual by: the persisted image,
 *  the live preview that replaced it in place, or the reason there is neither. */
function CellArtwork({
  url,
  alt,
  caption,
  gapReason,
  emptyLabel,
  onEnlarge,
}: {
  url: string | null
  alt: string
  caption: string
  gapReason: string | null
  emptyLabel: string
  onEnlarge: (image: EnlargedProductVisual) => void
}) {
  return (
    <div className="mt-3 aspect-square bg-muted/40">
      <button
        type="button"
        aria-label={alt}
        className="size-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => onEnlarge({ url, alt, caption })}
      >
        {url ? (
          <img src={url} alt={alt} loading="lazy" className="size-full object-contain" />
        ) : (
          <span className="flex size-full items-center justify-center px-4 text-center text-xs text-muted-foreground">
            {gapReason ? productVisualGapMessage(gapReason) : emptyLabel}
          </span>
        )}
      </button>
    </div>
  )
}

/** A live preview says so first — a cell that reverts on reload is the design,
 *  not a bug. It names both halves of what Render did, because they differ: the
 *  JPEG is session-ephemeral, the canonical content it re-resolved on the way is
 *  persisted. Saying only "not saved" would describe the image and quietly
 *  misdescribe the action. Otherwise the published image explains its provenance.
 *
 *  It also names the CATALOG-MIRROR COLUMNS the re-fill read, when the fill reports
 *  any (issue #349). That has to happen on THIS branch, not only on the attribution
 *  line below: `marketing-ingredients` is a `draft` kind, so it can only ever be
 *  previewed — the early return here is the only branch it reaches, and putting the
 *  columns on the published line alone would surface them exactly never. The kind
 *  runs on a stand-in column that is invisible in the pixels (it reuses the
 *  `ingredients` title), so the column name is the only way to tell which text is
 *  on the canvas. */
function metaLine(
  cell: ProductVisualCell,
  preview: ProductVisualRenderedPreview | undefined
): string {
  if (preview) {
    const filledFrom =
      preview.sourceColumns.length > 0 ? ` · filled from ${preview.sourceColumns.join(", ")}` : ""
    return `Preview image not saved · canonical content re-filled${filledFrom}`
  }
  if (!cell.artifact) {
    return cell.freshness === PRODUCT_VISUAL_FRESHNESS.new
      ? "Content ready · awaiting first image."
      : "No image."
  }
  const image = cell.artifact
  const attribution = image.source
    ? `${humanizeEnumLabel(image.source)}${image.attributionMode ? ` · ${humanizeEnumLabel(image.attributionMode)}` : ""}`
    : "attribution unavailable"
  return `v${image.templateVersion} · ${attribution} · ${formatDateTime(image.createdAt)}`
}

/** "Retry" is the label of a cell with nothing to show. Once a render answered
 *  with an image, the persisted `gap` is stale and the action is a plain Render
 *  again — a fresh gap keeps offering Retry. "Queued…" is a click that is waiting
 *  for the single global render slot; it says the click landed, so the operator
 *  does not press again. */
function renderLabel(input: {
  cell: ProductVisualCell
  renderStatus: ProductVisualRenderStatus
  hasLivePreview: boolean
}): string {
  if (input.renderStatus === "rendering") {
    return "Rendering…"
  }
  if (input.renderStatus === "queued") {
    return "Queued…"
  }
  if (input.hasLivePreview) {
    return "Render"
  }
  return input.cell.freshness === PRODUCT_VISUAL_FRESHNESS.gap ? "Retry" : "Render"
}

function canRegenerate(cell: ProductVisualCell): boolean {
  return (
    cell.freshness === PRODUCT_VISUAL_FRESHNESS.new ||
    cell.freshness === PRODUCT_VISUAL_FRESHNESS.stale ||
    cell.freshness === PRODUCT_VISUAL_FRESHNESS.excluded
  )
}

function hasActiveReplacement(cell: ProductVisualCell): boolean {
  return cell.replacement?.status === "queued" || cell.replacement?.status === "running"
}

function shouldShowRegenerate(
  cell: ProductVisualCell,
  onRegenerate: (() => void) | undefined
): onRegenerate is () => void {
  return onRegenerate !== undefined && canRegenerate(cell) && !hasActiveReplacement(cell)
}

function freshnessTone(cell: ProductVisualCell): "success" | "warning" | "muted" {
  if (cell.freshness === PRODUCT_VISUAL_FRESHNESS.current) {
    return "success"
  }
  return cell.freshness === PRODUCT_VISUAL_FRESHNESS.stale ? "warning" : "muted"
}

function FreshnessDetails({ cell }: { cell: ProductVisualCell }) {
  if (!cell.replacement && !cell.diff) {
    return null
  }
  return (
    <details className="rounded-lg border border-border/70 px-3 py-2 text-xs">
      <summary className="cursor-pointer font-medium text-muted-foreground">
        Why this changed
      </summary>

      {cell.replacement ? (
        <p className="mt-2 text-muted-foreground">
          {`Detected ${formatDateTime(cell.replacement.detectedAt)} · ${humanizeEnumLabel(cell.replacement.trigger)}`}
        </p>
      ) : null}

      {cell.diff?.state === "unavailable" ? (
        <p className="mt-2 text-muted-foreground">
          Input diff unavailable for this legacy artifact.
        </p>
      ) : null}

      {cell.diff?.state === "available" ? (
        <ul className="mt-2 space-y-1 font-mono text-[11px] text-muted-foreground">
          {cell.diff.changes.slice(0, 8).map((change) => (
            <li key={change.path}>{`${change.path}: ${change.before} → ${change.after}`}</li>
          ))}
        </ul>
      ) : null}
    </details>
  )
}
